import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, MessageCircle, Calendar, Gauge, Zap, Car, ChevronLeft, ChevronRight, X, Mail, Share2 } from 'lucide-react';
import { carPath } from '../lib/carRoutes';

const API = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP = "233544205844";
const COMPANY_EMAIL = "abbewray@runtheraysintl.com";

const CarDetail = () => {
  const { slug } = useParams();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  const fetchCar = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/cars/slug/${slug}`);
      setCar(data.data);
    } catch (err) {
      console.error('Failed to fetch car:', err);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchCar();
  }, [fetchCar]);

  const getImageUrl = (storagePath) => {
    const path = storagePath.replace('rtr-cars/cars/', '');
    return `${API}/api/cars/images/${path}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!car) {
    return (
      <div className="min-h-screen bg-gray-950 flex flex-col items-center justify-center text-white">
        <Car size={48} className="text-gray-700 mb-4" />
        <p className="text-lg mb-4">Car not found</p>
        <Link to={carPath("/cars")} className="text-orange-500 hover:underline">Back to showroom</Link>
      </div>
    );
  }

  const images = car.images || [];
  const whatsappMsg = encodeURIComponent(`Hi! I am interested in the ${car.title}.`);

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header */}
      <header className="bg-gray-900 border-b border-gray-800 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to={carPath("/cars")} className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors" data-testid="back-to-showroom">
            <ArrowLeft size={20} />
            <span className="text-sm font-medium">Back to Showroom</span>
          </Link>
          <a
            href={`https://wa.me/${WHATSAPP}?text=${whatsappMsg}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            data-testid="detail-whatsapp-btn"
          >
            <MessageCircle size={16} />
            <span>Inquire on WhatsApp</span>
          </a>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Image Gallery */}
          <div className="lg:col-span-3">
            {images.length > 0 ? (
              <>
                <div
                  className="relative aspect-[16/10] bg-gray-900 rounded-xl overflow-hidden cursor-pointer mb-3"
                  onClick={() => setLightbox(true)}
                  data-testid="main-image"
                >
                  <img src={getImageUrl(images[activeImg].storage_path)} alt={car.title} className="w-full h-full object-cover" />
                  <div className="absolute bottom-3 right-3 bg-black/70 text-white text-xs px-2 py-1 rounded">
                    {activeImg + 1} / {images.length}
                  </div>
                </div>
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {images.map((img, i) => (
                    <button
                      key={img.storage_path}
                      onClick={() => setActiveImg(i)}
                      className={`flex-shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 transition-colors ${i === activeImg ? 'border-orange-500' : 'border-transparent opacity-60 hover:opacity-100'}`}
                    >
                      <img src={getImageUrl(img.storage_path)} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <div className="aspect-[16/10] bg-gray-900 rounded-xl flex items-center justify-center">
                <Car size={64} className="text-gray-700" />
              </div>
            )}
          </div>

          {/* Car Info */}
          <div className="lg:col-span-2">
            <h1 className="text-2xl md:text-3xl font-bold mb-2" data-testid="car-title">{car.title}</h1>

            <div className="mb-6">
              {car.show_price && car.price ? (
                <span className="text-orange-500 font-bold text-2xl">${car.price.toLocaleString()}</span>
              ) : (
                <span className="text-gray-400 italic">Inquire for Price</span>
              )}
            </div>

            <div className="bg-gray-900 rounded-xl p-5 border border-gray-800 mb-6">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">Specifications</h3>
              <div className="space-y-3">
                <SpecRow icon={<Calendar size={16} />} label="Year" value={car.year} />
                <SpecRow icon={<Car size={16} />} label="Make / Model" value={`${car.make} ${car.model}`} />
                <SpecRow icon={<Gauge size={16} />} label="Mileage" value={car.mileage} />
                <SpecRow icon={<Zap size={16} />} label="Engine" value={car.engine} />
                <SpecRow icon={<Zap size={16} />} label="Power" value={car.power} />
              </div>
            </div>

            {car.description && (
              <div className="bg-gray-900 rounded-xl p-5 border border-gray-800 mb-6">
                <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Description</h3>
                <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-line">{car.description}</p>
              </div>
            )}

            <a
              href={`https://wa.me/${WHATSAPP}?text=${whatsappMsg}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white py-3 rounded-xl font-semibold text-base transition-colors"
              data-testid="detail-whatsapp-cta"
            >
              <MessageCircle size={20} /> Inquire on WhatsApp
            </a>
            <a
              href={`mailto:${COMPANY_EMAIL}?subject=Inquiry: ${encodeURIComponent(car.title)}`}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-semibold text-base transition-colors mt-3"
              data-testid="detail-email-cta"
            >
              <Mail size={20} /> Inquire via Email
            </a>
            <button
              onClick={async () => {
                const url = window.location.href;
                if (navigator.share) {
                  try { await navigator.share({ title: car.title, text: `Check out this ${car.title}`, url }); } catch (err) { console.error('Share failed:', err); }
                } else {
                  await navigator.clipboard.writeText(url);
                  alert('Link copied to clipboard!');
                }
              }}
              className="w-full flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 text-white py-3 rounded-xl font-semibold text-base transition-colors mt-3"
              data-testid="detail-share-cta"
            >
              <Share2 size={20} /> Share Listing
            </button>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && images.length > 0 && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" onClick={() => setLightbox(false)}>
          <button onClick={() => setLightbox(false)} className="absolute top-4 right-4 text-white/70 hover:text-white" data-testid="lightbox-close">
            <X size={28} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setActiveImg(p => (p - 1 + images.length) % images.length); }}
            className="absolute left-4 text-white/70 hover:text-white"
          >
            <ChevronLeft size={36} />
          </button>
          <img
            src={getImageUrl(images[activeImg].storage_path)}
            alt={car.title}
            className="max-w-[90vw] max-h-[85vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={(e) => { e.stopPropagation(); setActiveImg(p => (p + 1) % images.length); }}
            className="absolute right-4 text-white/70 hover:text-white"
          >
            <ChevronRight size={36} />
          </button>
          <div className="absolute bottom-4 text-white/70 text-sm">{activeImg + 1} / {images.length}</div>
        </div>
      )}
    </div>
  );
};

const SpecRow = ({ icon, label, value }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="flex items-center gap-2 text-gray-400">{icon} {label}</span>
    <span className="text-white font-medium">{value}</span>
  </div>
);

export default CarDetail;
